import { createContext } from 'preact'
import { useContext, useEffect, useState } from 'preact/hooks'
import { config } from '../config'

interface User {
  id: string
  email: string
  firstName?: string
  lastName?: string
  isAuthenticated: boolean
}

// Track authentication state
let isUserAuthenticated = false

interface AuthContextType {
  user: User | null
  isLoading: boolean
  error: string | null
  logout: () => void
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  error: null,
  logout: () => {},
  checkAuth: async () => {},
})

export function AuthProvider({ children }: { children: any }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const checkAuth = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Request user info from /user/me endpoint
      const response = await fetch(config.routePrefix + '/user/me', {
        credentials: 'include',
      })

      if (!response.ok) {
        // Silently handle 401 errors when not authenticated
        if (response.status === 401 && !isUserAuthenticated) {
          setUser(null)
          isUserAuthenticated = false
          return
        }
        throw new Error(`HTTP error ${response.status}`)
      }

      const data = await response.json()

      if (data.authenticated && data.user) {
        setUser({
          ...data.user,
          isAuthenticated: true,
        })
        isUserAuthenticated = true
      } else {
        setUser(null)
        isUserAuthenticated = false
      }
    } catch (err) {
      if (isUserAuthenticated) {
        console.error('Auth check failed:', err)
      }
      setError('Failed to verify authentication status')
      setUser(null)
      isUserAuthenticated = false
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      // Call the logout endpoint
      await fetch(config.routePrefix + '/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      setUser(null)
      isUserAuthenticated = false
    } catch (err) {
      console.error('Logout failed:', err)
      setUser(null)
      isUserAuthenticated = false
    }
  }

  // On the user, so signing out clears them without a second reset.

  // Initial auth check
  useEffect(() => {
    checkAuth()
  }, [])

  return <AuthContext.Provider value={{ user, isLoading, error, logout, checkAuth }}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext)
